import styled from '@emotion/styled';
import React, { type ReactNode, isValidElement } from 'react';

import type { LensDivProps } from '../../types';
import Checkbox from '../checkbox/checkbox';
import Radio from '../radio/radio';
import Switch from '../switch/switch';

const directionStyles = {
  row: {
    wrapper: {
      display: 'grid',
      gridTemplateColumns: 'auto 1fr',
      alignItems: 'center',
    },
    label: { marginLeft: 'var(--lns-space-small)' },
    errorMessage: { marginLeft: 'var(--lns-space-small)' },
  },
  'row-reverse': {
    wrapper: {
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      alignItems: 'center',
    },
    label: {},
  },
  column: {
    wrapper: {},
    label: { marginBottom: 'var(--lns-space-xsmall)' },
  },
};

const FormFieldWrapper = styled.div<FormFieldProps>`
  ${props => props.direction && directionStyles[props.direction].wrapper};
`;

const Label = styled.label<FormFieldProps>`
  display: block;
  ${props => props.direction && directionStyles[props.direction]?.label};
  ${props => props.isLabelClickable && 'cursor: pointer'};
`;

const ErrorMessage = styled.span`
  color: var(--lns-color-red);
  margin-top: var(--lns-space-xsmall);
  display: block;
  width: 100%;
  grid-column-start: 1;
  grid-column-end: 3;
`;

type InteractiveComponentType =
  | React.ComponentType<any>
  | React.ForwardRefExoticComponent<any>;

// Include any Lens Components that should have a clickable label here
const interactiveComponents: InteractiveComponentType[] = [
  Radio,
  Checkbox,
  Switch,
];

const isInteractiveComponent = (
  component: any
): component is InteractiveComponentType => {
  return interactiveComponents.includes(component);
};

const FormField = ({
  label,
  children,
  errorMessage,
  labelFor,
  direction = 'column',
  ...props
}: FormFieldProps &
  React.ComponentProps<typeof FormFieldWrapper> &
  Omit<
    LensDivProps,
    keyof FormFieldProps & React.ComponentProps<typeof FormFieldWrapper>
  >) => {
  // Checks if child component is interactive
  const isLabelClickable = React.Children.toArray(children).some(
    child =>
      isValidElement(child) &&
      // The child might be a div, span, etc (type string) so we
      // have to typeguard before we check if our child is of interactive
      typeof child.type !== 'string' &&
      isInteractiveComponent(child.type)
  );

  const labelElement = label && (
    <Label
      direction={direction}
      htmlFor={labelFor}
      isLabelClickable={isLabelClickable}
    >
      {label}
    </Label>
  );

  return (
    <FormFieldWrapper direction={direction} {...props}>
      {direction === 'row' && (
        <>
          {children}
          {labelElement}
        </>
      )}
      {direction === 'column' && (
        <>
          {labelElement}
          {children}
        </>
      )}
      {direction === 'row-reverse' && (
        <>
          {labelElement}
          {children}
        </>
      )}
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </FormFieldWrapper>
  );
};

type FormFieldProps = {
  label?: ReactNode;
  errorMessage?: string | null;
  labelFor?: string;
  children?: React.ReactNode;
  direction?: 'row' | 'column' | 'row-reverse';
  isLabelClickable?: boolean;
};

export default FormField;
